import React, {useEffect, useRef, useState} from 'react';
import {useRecoilState, useRecoilValue} from 'recoil';
import {AxiosResponse} from 'axios';
import {useLocation} from 'react-router-dom';

import {
  Communities,
  CommunityAddress1,
  CommunityAddress2,
  CommunityListArgs,
} from '@/store';
import {dummyCommunity} from '@/utils';
import UserContent from '@/components/community/UserContent';
import {getCommunities} from '@/services/community';
import Fetcher from '@/components/common/Fetcher';
import {user} from '@/store/user';
import {useScroll} from '@/hooks/useScroll';

import styles from './styles.module.scss';

export interface ListArgs {
  page: number;
  address1: string | undefined;
  address2: string | undefined;
}

export default function CommunityList() {
  const {syncScroll} = useScroll();
  const accessToken = useRecoilValue(user);
  const location = useLocation();
  const [lastElement, setLastElement] = useState<Element | null>(null);
  const [communities, setCommunities] = useRecoilState(Communities);
  const address1 = useRecoilValue(CommunityAddress1);
  const address2 = useRecoilValue(CommunityAddress2);
  const [args, setArgs] = useRecoilState(CommunityListArgs);
  useScroll();
  let totalCnt = 0;
  let communityLength = 0;
  const observer = useRef(
    new IntersectionObserver(entries => {
      const first = entries[0];
      if (first.isIntersecting && totalCnt > communityLength) {
        setArgs((a: ListArgs) => {
          return {
            ...a,
            page: a.page + 1,
          };
        });
      }
    }),
  );

  const makeDummyList = (p: number) => {
    const list: Community[] = Array<Community>(5).fill(dummyCommunity);
    const _communities = [...communities];
    setCommunities(p === 0 ? [...list] : [..._communities, ...list]);
  };

  const getList = async (_args: typeof args | undefined) => {
    const page = _args?.page || 0;
    makeDummyList(page);
    const _params = location.state?.id
      ? {
          offset: 0,
          limit: page * 5 + 5,
          brand_group: location.state?.id,
        }
      : {
          address1: address1.length > 0 ? address1 : undefined,
          address2: address2.length > 0 ? address2 : undefined,
          offset: 0,
          limit: page * 5 + 5,
        };
    const res: AxiosResponse<CommunityRes> = await getCommunities(
      accessToken,
      _params,
    );
    const exceptDummy = communities.filter(c => c.id !== -1);
    const lastIdx = res.data.results.length - 5;
    const _communities =
      page === 0
        ? [...res.data.results]
        : [...exceptDummy, ...res.data.results.splice(lastIdx, 5)];
    totalCnt = res.data.count;
    communityLength = _communities.length;
    return _communities;
  };

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement]);

  useEffect(() => {
    if (location.state?.id) {
      setCommunities([]);
      scrollTo(0, 0);
    }
  }, [location]);

  return (
    <Fetcher
      fetcher={getList}
      setter={setCommunities}
      args={args}
      syncScroll={syncScroll}
      noLoader>
      {communities.length > 0 && (
        <div className={styles.community_contents}>
          {communities.map((community, idx) => {
            return (
              <div
                ref={setLastElement}
                key={`user_contents_${
                  community.id > -1 ? community.id : idx
                }_${idx}`}>
                <UserContent community={community} wrapperType={'community'} />
              </div>
            );
          })}
        </div>
      )}
      {communities.length === 0 && (
        <div className="no-community-contents">게시글이 없습니다.</div>
      )}
    </Fetcher>
  );
}
